<template>
  <BaseLayout>
    <BaseDashboard :is-loading="false">
      <div class="h-100 overflow-auto">
        <b-row>
          <b-col>
            <b-card
              header-tag="header"
              body-class="bg-secondary p-0"
            >
              <div
                class="card-options"
                slot="header"
              >
                <b-button
                  data-test="add-template"
                  variant="primary"
                  size="lg"
                  :to="{ name: 'TemplateCreate' }"
                >
                  New template
                </b-button>
              </div>
              <b-row class="p-5">
                <b-col>
                  <fieldset class="b-form-group form-group">
                    <legend class="col-form-label form-label">
                      Filters
                    </legend>
                    <div
                      role="group"
                      class="d-flex"
                    >
                      <b-form-group
                        label="Name"
                        label-for="search-template"
                        class="pr-3 w-75"
                      >
                        <div class="d-flex">
                          <base-autocomplete
                            id="search-template"
                            class="w-75 mr-4"
                            data-test="name-filter"
                            :items="filteredTemplatesOptions"
                            v-model="nameModel"
                            :search-text.sync="nameFilter"
                            @update:searchText="onFilterChange"
                            @keyup.native="onKeyUp"
                          />
                          <span
                            v-if="filterItemsLoading"
                            class="loader"
                          />
                        </div>
                      </b-form-group>
                    <!-- <b-form-group
                      class="pr-3"
                      label="Sizes"
                      label-for="sizes"
                    >
                      <b-form-select
                        id="sizes"
                        class="w-auto"
                        data-test="filter-size"
                        v-model="selectedSize"
                        :options="sizesOptions"
                        @change="onFilterChange"
                      />
                    </b-form-group>
                    <b-form-group
                      class="pr-3"
                      label="Types"
                      label-for="types"
                    >
                      <b-form-select
                        id="types"
                        class="w-auto"
                        data-test="filter-type"
                        v-model="selectedType"
                        :options="typeOptions"
                        @change="onFilterChange"
                      />
                    </b-form-group> -->
                    </div>
                  </fieldset>
                </b-col>
                <b-col class="d-md-flex align-items-center justify-content-end">
                  <base-pagination
                    data-test="template-pagination"
                    hide-page-select
                    :items-count="itemsCount"
                    :current-page.sync="currentPage"
                    :per-page-selected.sync="perPageSelected"
                    items-name="templates"
                  />
                </b-col>
              </b-row>

              <b-table
                show-empty
                fixed
                data-test="templates-list"
                class="mb-0 templates-table"
                ref="templates"
                :busy.sync="isLoading"
                :fields="headers"
                :items="itemsProvider"
                :per-page="perPageSelected"
                :current-page="currentPage"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
              >
                <div
                  slot="table-busy"
                  class="align-items-center d-flex justify-content-center card-body"
                  data-test="templates-list-loader"
                >
                  <div class="loader mx-auto" />
                </div>
                <template
                  #cell(name)="data"
                >
                  <div
                    class="px-2"
                    data-test="template-name"
                  >
                    {{ data.item.name }}
                  </div>
                  <b-button
                    size="sm"
                    variant="link"
                    data-test="template-update-link"
                    :to="{ name: 'TemplateUpdate', params: { id: data.item.id } }"
                  >
                    Edit
                  </b-button>
                </template>
                <template
                  slot="lastUpdated"
                  slot-scope="data"
                >
                  {{ data.value }}
                </template>
              </b-table>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </BaseDashboard>
  </BaseLayout>
</template>

<script>
import {
  mapActions,
} from 'vuex';
import * as campaignsTypes from '@/modules/campaigns/store/types';
import * as templatesTypes from '@/modules/templates/store/types';
import helper from '@/core/helper';
import paginationMixin from '@/core/mixins/pagination';
import { TYPES, SIZES } from '@/modules/templates/templates.constants';
import BasePagination from '@/components/BasePagination.vue';
import BaseAutocomplete from '@/components/form/BaseAutocomplete.vue';

export default {
  name: 'Templates',
  components: { BaseAutocomplete, BasePagination },

  mixins: [paginationMixin],

  data() {
    return {
      headers: [
        {
          key: 'name',
          sortable: true,
        },
        {
          key: 'type',
          sortable: true,
          formatter: (value, key, item) => this.getFormattedType(item.typeId),
        },
        {
          key: 'size',
          sortable: true,
          formatter: (value, key, item) => this.getFormattedSize(item.sizeId),
        },
        {
          key: 'lastUpdated',
          sortable: true,
          formatter: (value) => helper.formatDate(value),
        },
      ],
      pageOffset: 1,
      currentPage: 1,
      perPageSelected: 30,
      isLoading: false,
      itemsCount: 0,
      sortBy: '',
      sortDesc: false,
      selectedSize: '',
      selectedType: '',
      nameModel: '',
      nameFilter: '',
      filteredTemplatesOptions: [],
      creativeSizes: [],
      creativeTypes: [],
      typeOptions: [
        { text: 'All', value: '' },
        ...TYPES,
      ],
      filterItemsLoading: false,
    };
  },

  computed: {
    filters() {
      return {
        size: this.selectedSize,
        type: this.selectedType,
        name: this.nameFilter,
      };
    },

    sizesOptions() {
      return Object.values(SIZES)
        .reduce(
          (allSizes, sizes) => allSizes.concat(sizes),
          [{ text: 'All', value: '' }],
        );
    },
  },

  methods: {
    ...mapActions('campaigns', [
      campaignsTypes.FETCH_CREATIVE_SIZES,
    ]),
    ...mapActions('templates', [
      templatesTypes.FETCH_TEMPLATES_SEARCH,
      templatesTypes.FETCH_CREATIVE_TYPES,
    ]),
    itemsProvider(context) {
      const {
        currentPage,
        perPage,
        sortBy,
        sortDesc,
      } = context;
      const params = {
        page: currentPage,
        perPage,
        sortBy,
        sortDesc,
      };
      return this.FETCH_TEMPLATES_SEARCH({ params, filters: this.filters })
        .then((templatesData) => {
          const { creativeTemplateResponses, total } = templatesData;
          this.itemsCount = total;
          return creativeTemplateResponses;
        })
        .finally(() => {
          const query = {
            sortBy,
            sortDesc,
            currentPage,
            name: this.filters.name,
          };
          this.$router.replace({ name: 'templates', query });
        });
    },

    onKeyUp: helper.debounce(function getTemplatesFiltered(event) {
      this.filterItemsLoading = true;
      const filters = {
        name: event.target.value,
        size: this.selectedSize,
        type: this.selectedType,
      };
      this.FETCH_TEMPLATES_SEARCH({ params: {}, filters })
        .then((templatesData) => {
          this.filteredTemplatesOptions = templatesData.creativeTemplateResponses;
        })
        .finally(() => {
          this.filterItemsLoading = false;
        });
    }, 500),

    resetPage() {
      this.currentPage = 1;
      this.pageOffset = 1;
    },

    onFilterChange() {
      this.resetPage();
      this.$refs.templates.refresh();
    },
    getFormattedSize(id) {
      const creativeItem = this.creativeSizes
        .find((creative) => creative.id === id);
      return creativeItem ? `${creativeItem.width}x${creativeItem.height}` : '';
    },
    getFormattedType(id) {
      const creativeType = this.creativeTypes
        .find((types) => types.id === id);
      return creativeType ? creativeType.name : '';
    },
  },

  watch: {
    currentPage() {
      this.pageOffset = this.calculatePageOffset(this.currentPage, this.perPageSelected);
    },
    perPageSelected(newVal, oldVal) {
      this.pageOffset = this.calculatePageOffset(this.currentPage, oldVal);
    },
  },

  async created() {
    const {
      pageOffset,
      currentPage,
      sortBy,
      sortDesc,
      size,
      type,
      name,
    } = this.$route.query;
    this.pageOffset = parseInt(pageOffset, 10) || 1;
    this.currentPage = parseInt(currentPage, 10) || 1;
    this.sortBy = sortBy || '';
    this.sortDesc = (sortDesc) ? JSON.parse(sortDesc) : false;
    this.selectedSize = size || '';
    this.selectedType = type || '';
    this.nameModel = name || '';
    this.nameFilter = name || '';
    await this.FETCH_CREATIVE_SIZES()
      .then((sizes) => { this.creativeSizes = sizes; });
    await this.FETCH_CREATIVE_TYPES()
      .then((types) => { this.creativeTypes = types; });

    // this.isLoading = false;
  },
};
</script>

<style lang="scss" scoped>
.templates-table .is-hidden {
  visibility: hidden;
}

.templates-table tr:hover {
  .is-hidden {
      visibility: visible;
  }
}
.templates-table td {
  vertical-align: middle;
}
</style>
